import { type ComponentChildren, createContext } from 'preact'
import { useCallback, useContext } from 'preact/hooks'
import type { ConversationSummary } from '../api/generated/ConversationSummary'
import type { GroupSummary } from '../api/generated/GroupSummary'
import { useApi } from '../api/useApi'
import { useSignalReload } from '../live/useLiveReload'

/** Members of "active now" groups change as shifts start and end, without any signal. */
const PERIODIC_RELOAD_MS = 60_000

type VolunteerThreads = {
  conversations: ReturnType<typeof useApi<ConversationSummary[]>>
  groups: ReturnType<typeof useApi<GroupSummary[]>>
  reload: () => void
}

const VolunteerThreadsContext = createContext<VolunteerThreads | null>(null)

/** A volunteer's conversation with the organizers and groups, kept live for the badge and the messaging. */
export function VolunteerThreadsProvider({
  slug,
  children,
}: {
  slug: string
  children: ComponentChildren
}) {
  const conversations = useApi<ConversationSummary[]>(`/events/${slug}/conversations`)
  const groups = useApi<GroupSummary[]>(`/events/${slug}/groups`)
  const reload = useCallback(() => {
    conversations.reload()
    groups.reload()
  }, [conversations.reload, groups.reload])
  useSignalReload(`/events/${slug}/conversations/live`, reload, PERIODIC_RELOAD_MS)
  return (
    <VolunteerThreadsContext.Provider value={{ conversations, groups, reload }}>
      {children}
    </VolunteerThreadsContext.Provider>
  )
}

export function useVolunteerThreads(): VolunteerThreads {
  const threads = useContext(VolunteerThreadsContext)
  if (!threads) throw new Error('useVolunteerThreads outside VolunteerThreadsProvider')
  return threads
}
